<template>
    <main class="evl-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('events.page.title') }}</div>
        </portal>

        <header class="evl-hero">
            <div>
                <h1 class="evl-hero-title">{{ $t('events.page.title') }}</h1>
                <p class="evl-hero-sub">{{ $t('events.page.subtitle') }}</p>
            </div>

            <div class="evl-hero-actions">
                <nav class="evl-panel-toggle">
                    <button
                        type="button"
                        class="evl-toggle-btn"
                        :class="{ 'is-active': panel === 'table' }"
                        @click="panel = 'table'"
                    >
                        <icon-list class="evl-toggle-icon" />
                        <span class="evl-toggle-label">{{ $t('events.page.panel_table') }}</span>
                    </button>
                    <button
                        type="button"
                        class="evl-toggle-btn"
                        :class="{ 'is-active': panel === 'map' }"
                        @click="panel = 'map'"
                    >
                        <icon-globe class="evl-toggle-icon" />
                        <span class="evl-toggle-label">{{ $t('events.page.panel_map') }}</span>
                    </button>
                </nav>

                <BmvButton
                    v-if="canCreate"
                    variant="primary"
                    icon="fal fa-plus"
                    @click="$router.push({ name: 'EventCreate' })"
                >
                    {{ $t('events.page.new') }}
                </BmvButton>
            </div>
        </header>

        <EventsFilters :value="filters" @change="onFiltersChange" />

        <div v-if="panel === 'table'">
            <BmvTable
                :columns="tableColumns"
                :data="eventsList"
                :loading="isLoading"
                :pagination="true"
                :total-results="totalResults"
                :total-pages="totalPages"
                :current-page="currentPage"
                :per-page="eventsConfig.per_page"
                :sort-field="eventsConfig.sort"
                :sort-direction="eventsConfig.sortDirection"
                :empty-text="$t('events.page.empty')"
                @sort="onSort"
                @page-change="onPageChange"
            >
                <template #cell-title="{ row }">
                    <router-link :to="`/events/${row.id}`" class="bmv-cell-link">
                        {{ row.title || '—' }}
                    </router-link>
                </template>

                <template #cell-type="{ row }">
                    <span class="ev-badge" :class="`ev-badge--${row.type}`">
                        <i :class="row.type === 'event' ? 'fal fa-calendar-alt' : 'fal fa-project-diagram'"></i>
                        {{ $t(`events.type.${row.type}`) }}
                    </span>
                </template>

                <template #cell-status="{ row }">
                    <span class="ev-badge" :class="`ev-badge--${row.status}`">
                        <i :class="statusIcon(row.status)"></i>
                        {{ $t(`events.status.${row.status}`) }}
                    </span>
                </template>

                <template #cell-start_date="{ row }">
                    <span v-if="row.start_date">{{ formatDate(row.start_date) }}</span>
                    <span v-else class="ev-muted">—</span>
                </template>

                <template #cell-end_date="{ row }">
                    <span v-if="row.end_date">{{ formatDate(row.end_date) }}</span>
                    <span v-else class="ev-muted">—</span>
                </template>

                <template #cell-author="{ row }">
                    <span v-if="row.author">{{ row.author.name }}</span>
                    <span v-else class="ev-muted">—</span>
                </template>

                <template #cell-actions="{ row }">
                    <div class="ev-actions">
                        <BmvButton
                            variant="ghost"
                            size="sm"
                            icon="fal fa-eye"
                            :title="$t('common.view')"
                            @click="$router.push({ name: 'EventView', params: { id: row.id } })"
                        />
                        <BmvButton
                            v-if="canUpdate"
                            variant="ghost"
                            size="sm"
                            icon="fal fa-pen"
                            :title="$t('common.edit')"
                            @click="$router.push({ name: 'EventEdit', params: { id: row.id } })"
                        />
                        <BmvButton
                            v-if="canUpdate && row.status === 'draft'"
                            variant="success"
                            size="sm"
                            icon="fal fa-paper-plane"
                            :title="$t('events.actions.publish')"
                            :loading="busyId === row.id"
                            @click="doPublish(row)"
                        />
                        <BmvButton
                            v-else-if="canUpdate && row.status === 'published'"
                            variant="secondary"
                            size="sm"
                            icon="fal fa-ban"
                            :title="$t('events.actions.unpublish')"
                            :loading="busyId === row.id"
                            @click="doUnpublish(row)"
                        />
                        <BmvButton
                            v-if="canUpdate && row.status !== 'cancelled'"
                            variant="neutral"
                            size="sm"
                            icon="fal fa-times-circle"
                            :title="$t('events.actions.cancel')"
                            :loading="busyId === row.id"
                            @click="doCancel(row)"
                        />
                        <BmvButton
                            v-if="canDelete"
                            variant="danger"
                            size="sm"
                            icon="fal fa-trash"
                            :title="$t('common.delete')"
                            @click="confirmDelete(row)"
                        />
                    </div>
                </template>
            </BmvTable>
        </div>

        <div v-else>
            <EventsMap :events="allEventsForMap" :key="'map-' + mapKey" />
        </div>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvTable from '@/components/UI/BmvTable.vue'
import EventsFilters from '@/components/Events/EventsFilters.vue'
import EventsMap from '@/components/Events/EventsMap.vue'
import IconList from '@/components/icons/iconList.vue'
import IconGlobe from '@/components/icons/iconGlobe.vue'

export default {
    name: 'EventsList',
    components: { BmvButton, BmvTable, EventsFilters, EventsMap, IconList, IconGlobe },
    data() {
        return {
            isLoading: false,
            busyId: null,
            panel: 'table',
            mapKey: 0,
            tableColumns: [
                { field: 'title', label: this.$t('events.table.title'), sortable: true, width: '30%' },
                { field: 'type', label: this.$t('events.table.type'), width: '11%' },
                { field: 'status', label: this.$t('events.table.status'), width: '11%' },
                { field: 'start_date', label: this.$t('events.table.start_date'), sortable: true, width: '12%' },
                { field: 'end_date', label: this.$t('events.table.end_date'), width: '12%' },
                { field: 'author', label: this.$t('events.table.author'), width: '14%' },
                { field: 'actions', label: this.$t('events.table.actions'), width: '160px', align: 'right' }
            ]
        }
    },
    computed: {
        ...mapState('events', {
            events: (state) => state.events,
            eventsConfig: (state) => state.eventsConfig
        }),
        filters() {
            return this.eventsConfig.filters || {}
        },
        eventsList() {
            return (this.events && this.events.data) || []
        },
        allEventsForMap() {
            return this.eventsList
        },
        totalResults() {
            return (this.events && this.events.total) || 0
        },
        currentPage() {
            return (this.events && this.events.current_page) || 1
        },
        totalPages() {
            if (!this.events || !this.events.total || !this.events.per_page) return 0
            return Math.ceil(this.events.total / this.events.per_page)
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canCreate() {
            return this.permissions.includes('events.create')
        },
        canUpdate() {
            return this.permissions.includes('events.update')
        },
        canDelete() {
            return this.permissions.includes('events.delete')
        }
    },
    created() {
        this.load()
    },
    methods: {
        ...mapActions('events', [
            'getEvents',
            'deleteEvent',
            'publishEvent',
            'unpublishEvent',
            'cancelEvent'
        ]),
        statusIcon(status) {
            const icons = {
                draft: 'fal fa-pen',
                published: 'fal fa-check-circle',
                cancelled: 'fal fa-times-circle'
            }
            return icons[status] || 'fal fa-circle'
        },
        formatDate(iso) {
            if (!iso) return ''
            return this.moment(iso).format('L')
        },
        load() {
            this.isLoading = true
            return this.getEvents()
                .catch((err) => this.$store.dispatch('dispatchError', err))
                .finally(() => {
                    this.isLoading = false
                    this.mapKey++
                })
        },
        onFiltersChange(filters) {
            this.eventsConfig.filters = { ...filters }
            this.eventsConfig.page = 1
            this.load()
        },
        onSort({ field, direction }) {
            this.eventsConfig.sort = field
            this.eventsConfig.sortDirection = direction
            this.eventsConfig.page = 1
            this.load()
        },
        onPageChange(page) {
            this.eventsConfig.page = page
            this.load()
        },
        async doPublish(row) {
            if (this.busyId) return
            this.busyId = row.id
            try {
                await this.publishEvent(row.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.published') })
                await this.load()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyId = null
            }
        },
        async doUnpublish(row) {
            if (this.busyId) return
            this.busyId = row.id
            try {
                await this.unpublishEvent(row.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.unpublished') })
                await this.load()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyId = null
            }
        },
        async doCancel(row) {
            if (this.busyId) return
            const confirmed = this.$bmvDialog && typeof this.$bmvDialog.confirm === 'function'
                ? await this.$bmvDialog.confirm({
                    variant: 'danger',
                    title: this.$t('events.actions.cancel_confirm'),
                    confirmLabel: this.$t('events.actions.cancel'),
                    cancelLabel: this.$t('common.cancel')
                })
                : window.confirm(this.$t('events.actions.cancel_confirm'))
            if (!confirmed) return
            this.busyId = row.id
            try {
                await this.cancelEvent(row.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.cancelled') })
                await this.load()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyId = null
            }
        },
        async confirmDelete(row) {
            const confirmed = this.$bmvDialog && typeof this.$bmvDialog.confirm === 'function'
                ? await this.$bmvDialog.confirm({
                    variant: 'danger',
                    title: this.$t('events.actions.delete_confirm'),
                    confirmLabel: this.$t('common.delete'),
                    cancelLabel: this.$t('common.cancel')
                })
                : window.confirm(this.$t('events.actions.delete_confirm'))
            if (!confirmed) return
            try {
                await this.deleteEvent(row.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('common.delete') })
                await this.load()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            }
        }
    }
}
</script>

<style scoped>
.evl-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.evl-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: var(--v2-space-6);
    flex-wrap: wrap;
}

.evl-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.evl-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.evl-hero-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.evl-panel-toggle {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #f1f5f9;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 4px;
}

.evl-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 8px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #64748b;
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    transition: all 0.15s ease;
    white-space: nowrap;
}

.evl-toggle-btn:hover {
    color: #0891B2;
    background: rgba(8, 145, 178, 0.06);
}

.evl-toggle-btn.is-active {
    background: #fff;
    color: #0891B2;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.evl-toggle-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: #94a3b8;
}

.evl-toggle-btn:hover .evl-toggle-icon,
.evl-toggle-btn.is-active .evl-toggle-icon {
    color: #0891B2;
}

/* Status / type badges */
.ev-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.ev-badge i {
    font-size: 0.7rem;
}

/* Type */
.ev-badge--event {
    background: #e0f2fe;
    color: #0369a1;
}

.ev-badge--project {
    background: #f0fdf4;
    color: #166534;
}

/* Status */
.ev-badge--draft {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.ev-badge--published {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.ev-badge--cancelled {
    background: #fef2f2;
    color: #b91c1c;
}

.ev-muted {
    color: var(--v2-text-4);
}

.ev-actions {
    display: inline-flex;
    gap: 6px;
    justify-content: flex-end;
}

@media (max-width: 640px) {
    .evl-toggle-label {
        display: none;
    }

    .evl-toggle-btn {
        padding: 6px 10px;
    }
}

@media (max-width: 900px) {
    .evl-v2 {
        padding: 16px 18px 32px;
    }

    .evl-hero {
        flex-direction: column;
        align-items: stretch;
        gap: var(--v2-space-3);
    }

    .evl-hero-title {
        font-size: 1.375rem;
    }

    .evl-hero-actions {
        flex-wrap: wrap;
    }
}
</style>
